import { IconType } from '@/types/icon.types';

import type { MenuInfo } from '@/common/components/organisms/baseMenu/types';
import { MenuSearchResultItem } from '@/common/components/organisms/menuSearch/menuSearch.type';

/**
 * MenuInfo의 value에서 아이콘 클래스명 추출
 * @param value - MenuInfo의 value
 * @returns 아이콘 클래스명 (예: icon--infrastructure)
 */
export const getIconFromValue = (value: string): string => {
  const iconName = value.split('_')[0].toLowerCase();
  return `icon--${iconName}`;
};

/**
 * MenuSearchResultItem 아이콘 생성
 * - menu: getIconFromValue(rootValue) 기반이지만, MenuSearchResultItem.vue CSS 매칭을 위해 `icon-xxx`로 정규화
 * - dashboard: featuredIcon 우선, 없으면 `icon-dashboard`로 fallback
 * @param isDashboard - 대시보드 타입 여부
 * @param rootValue - 메뉴의 root value (menu 타입일 때만 필요)
 * @param featuredIcon - 대시보드의 featuredIcon (dashboard 타입일 때 선택적)
 */
export const getMenuSearchItemIcon = ({
  isDashboard,
  rootValue,
  featuredIcon,
}: {
  isDashboard: boolean;
  rootValue?: string;
  featuredIcon?: IconType;
}): string => {
  if (isDashboard) {
    return featuredIcon || 'icon--dashboard';
  }

  // menu 타입일 때는 rootValue가 필수
  if (!rootValue) {
    return 'icon--dashboard'; // fallback
  }

  // getIconFromValue는 icon--xxx 형태를 반환하므로, 아이템 렌더링용으로 icon-xxx로 변환
  return getIconFromValue(rootValue);
};

/**
 * 메뉴 노드가 "섹션 타이틀(그룹 헤더)" 인지 자동 판별한다.
 *
 * 사이드바 패널(`RightPanelMenu`)이 mid-menu(회색 헤더)로 렌더링하는 분류 기준을
 * 그대로 차용한다:
 *   `<right-panel-sub-menu-element v-if="!info.children?.length" />`
 *   `<li v-else class="mid-menu">{{ info.text }}</li>`
 *
 * 판별 기준:
 * - `path` 가 있으면 클릭 가능한 leaf → false
 * - `subMenuList` 만 있으면 일반 메뉴(클릭 가능 컨테이너) → false
 *   (예: `oracle`, `cloud_aws`, `kubernetes_performanceAnalysis`)
 * - `children` 이 있어도 자식 중 또 다른 그룹(children 보유)이 있으면
 *   의미있는 컨테이너 → false (예: `database`, `kubernetes`, `cloud_aws`)
 */
export const isSectionTitle = (menu: MenuInfo): boolean => {
  if (menu.path) {
    return false;
  }
  if (!menu.children?.length) {
    return false;
  }
  const hasSubGroup = menu.children.some((child) => !!child.children?.length);
  return !hasSubGroup;
};

const stripQueryString = (path: string): string => {
  const queryIndex = path.indexOf('?');
  return queryIndex === -1 ? path : path.slice(0, queryIndex);
};

const getMenuChildLists = (menu: MenuInfo): MenuInfo[][] =>
  [
    menu.children,
    menu.subMenuList,
    menu.dashboardList?.subMenuList,
    menu.analysisBoardList?.subMenuList,
  ].filter((children): children is MenuInfo[] => !!children?.length);

const buildSubTitleFromTrail = (trail: MenuInfo[]): string => {
  return trail
    .slice(0, -1)
    .filter((menu, index) => {
      if (index === 0) {
        return !!menu.value;
      }
      return !!menu.value && !isSectionTitle(menu);
    })
    .map((menu) => menu.text)
    .filter((text): text is string => !!text)
    .join(' / ');
};

/**
 * 메뉴 트리를 1회 DFS 하여 path → subTitle(breadCrumbs) 매핑을 빌드한다.
 * @param menuInfo - 전체 메뉴 정보
 * @param t - 번역 함수. `/dashboard/list` 특수 라벨 처리에 사용
 * @returns path → subTitle 맵
 */
export const buildPathToSubTitleMap = (
  menuInfo: MenuInfo[],
  t: (key: string) => string,
): Map<string, string> => {
  const map = new Map<string, string>();

  const visit = (nodes: MenuInfo[], trail: MenuInfo[]) => {
    for (const node of nodes) {
      trail.push(node);

      if (node.path && !map.has(node.path)) {
        map.set(node.path, buildSubTitleFromTrail(trail));
      }

      for (const children of getMenuChildLists(node)) {
        visit(children, trail);
      }

      trail.pop();
    }
  };

  visit(menuInfo, []);

  // 메뉴 트리에 직접 들어있지 않은 특수 경로
  map.set('/dashboard/list', t('WORD.GNB.DASHBOARD_LIST'));

  return map;
};

/**
 * path 로 subTitle 조회. queryString 이 붙은 경우 떼고 lookup.
 */
export const lookupSubTitle = (
  pathToSubTitleMap: Map<string, string> | undefined,
  path: string | undefined,
): string => {
  if (!path || !pathToSubTitleMap) return '';
  const cleanPath = stripQueryString(path);
  return pathToSubTitleMap.get(cleanPath) ?? pathToSubTitleMap.get(path) ?? '';
};

/**
 * MenuInfo 배열을 그룹화하여 MenuSearchResultGroup 배열로 변환
 * subTitle 은 평탄화 단계(`flattenMenuItems`)에서 path → subTitle 맵 lookup 으로
 * 이미 계산되어 있으므로 여기서는 재계산하지 않는다.
 * @param items - 평탄화된 MenuSearchResultItem 배열
 * @param t - 번역 함수
 * @returns 그룹화된 MenuSearchResultGroup 배열
 */
export const groupMenuSearchResults = (
  items: MenuSearchResultItem[],
  t: (key: string) => string,
): Array<{ depthTitle: string; items: MenuSearchResultItem[] }> => {
  // 대시보드/분석보드의 depthTitle을 번역된 텍스트로 변경
  const processedItems = items.map((item) => {
    let processedSubTitle = item.subTitle;
    let processedTitle = item.title;

    if (item.type === 'menu' && item.path) {
      const cleanPath = stripQueryString(item.path);
      if (cleanPath === '/dashboard/list') {
        processedTitle = t('WORD.GNB.DASHBOARD_LIST');
        processedSubTitle = item.title;
      }
    }

    if (item.type === 'dashboard') {
      if (item.dashboardSubType === 'analysisBoard') {
        return {
          ...item,
          depthTitle: t('WORD.GNB.ANALYSISBOARD'),
          title: processedTitle,
          subTitle: processedSubTitle,
        };
      }
      return {
        ...item,
        depthTitle: t('WORD.GNB.DASHBOARD'),
        title: processedTitle,
        subTitle: processedSubTitle,
      };
    }

    return {
      ...item,
      title: processedTitle,
      subTitle: processedSubTitle,
    };
  });

  // 메뉴 아이템과 대시보드 아이템 분리
  const menuItems = processedItems.filter((item) => item.type === 'menu');
  const dashboardItems = processedItems.filter((item) => item.type === 'dashboard');

  // 메뉴 아이템 그룹화
  const menuGroupMap = new Map<string, MenuSearchResultItem[]>();
  const menuDepthTitleOrder: string[] = [];

  menuItems.forEach((item) => {
    if (!menuGroupMap.has(item.depthTitle)) {
      menuGroupMap.set(item.depthTitle, []);
      menuDepthTitleOrder.push(item.depthTitle);
    }
    menuGroupMap.get(item.depthTitle)!.push(item);
  });

  // 대시보드 아이템 그룹화
  const dashboardGroupMap = new Map<string, MenuSearchResultItem[]>();
  const dashboardDepthTitleOrder: string[] = [];

  dashboardItems.forEach((item) => {
    if (!dashboardGroupMap.has(item.depthTitle)) {
      dashboardGroupMap.set(item.depthTitle, []);
      dashboardDepthTitleOrder.push(item.depthTitle);
    }
    dashboardGroupMap.get(item.depthTitle)!.push(item);
  });

  // 메뉴 그룹을 먼저, 대시보드 그룹을 나중에 배치
  const menuGroups = menuDepthTitleOrder.map((depthTitle) => ({
    depthTitle,
    items: menuGroupMap.get(depthTitle) || [],
  }));

  const dashboardGroups = dashboardDepthTitleOrder.map((depthTitle) => ({
    depthTitle,
    items: dashboardGroupMap.get(depthTitle) || [],
  }));

  return [...menuGroups, ...dashboardGroups];
};
